import { useEffect, useState } from "react";
import { EditableObject } from "../bean/EditableObject";
import logo_close from "../assets/close-check.png";
import ListSelector from "./ListSelector";
import AuthServices from "../services/AuthServices";
import ImportedObjectServices from "../services/ImportedObjectServices";
import reservationServices from "../services/ReservationServices";
import "../styles/ObjectEditor.css";

function ObjectEditor({ object, onCancel, onSave, readonly = false, isDaataRegistering = false, onReserveByOwner, p_setLightboxImg, p_key }) {
  const [localObj, setLocalObj] = useState(new EditableObject(object));
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completeMsg, setCompleteMsg] = useState("");
  const [showReserver, setShowReserver] = useState(false);
  const [reserverInfo, setReserverInfo] = useState(null);
  const [isLoadingReserver, setIsLoadingReserver] = useState(false);
  const [reserverError, setReserverError] = useState("");

  useEffect(() => {
    if (!object) setLocalObj(new EditableObject());
    else setLocalObj(new EditableObject(object));
  }, [object]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalObj((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    onSave && onSave(localObj);
  };

  const handleReserve = () => setShowConfirmModal(true);
  const confirmReservation = () => {
    setShowConfirmModal(false);
    onReserveByOwner && onReserveByOwner();
  };

  const chooseImage = (imp) => {
    if (imp?.image) return imp.image;
    if (Array.isArray(imp?.imageList)) {
      const first = imp.imageList.find(Boolean);
      if (first) return first;
    }
    return undefined;
  };

  const handleAutoComplete = async () => {
    if (!localObj?.url) {
      setCompleteMsg("Renseignez l'URL avant de completer.");
      return;
    }
    setIsCompleting(true);
    setCompleteMsg("");
    try {
      const imp = await ImportedObjectServices.fetchObjectFromUrl(localObj.url);
      if (!imp) {
        setCompleteMsg("Aucune information trouvee.");
        return;
      }
      const updates = {};
      const fill = (key, value) => {
        const cur = localObj[key];
        if ((cur === undefined || cur === null || cur === "") && value !== undefined && value !== null && value !== "") {
          updates[key] = value;
        }
      };
      fill("title", imp.title || imp.shortTitle);
      fill("description", imp.description);
      fill("imagelink", chooseImage(imp));
      fill("price", imp.price);
      fill("currency", imp.currency);
      fill("site", imp.site);
      fill("url", imp.url);

      if (Object.keys(updates).length > 0) {
        setLocalObj((prev) => ({ ...prev, ...updates }));
        setCompleteMsg("Champs completes.");
      } else {
        setCompleteMsg("Aucune info trouvée pour compléter.");
      }
    } catch (e) {
      console.error(e);
      setCompleteMsg("Erreur pendant la recuperation.");
    } finally {
      setIsCompleting(false);
    }
  };

  const isEmptyField = (v) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");
  const hasEmptyFields = ["title", "description", "imagelink", "price", "site", "url"].some((k) =>
    isEmptyField(localObj?.[k])
  );

  const isReserved = localObj && localObj.reservationid != null;

  const handleShowReserver = async () => {
    if (!isReserved) return;
    const togglingTo = !showReserver;
    setShowReserver(togglingTo);
    if (togglingTo && !reserverInfo) {
      try {
        setIsLoadingReserver(true);
        setReserverError("");
        const key = AuthServices.getConnectedKey() || Date.now();
        const info = await reservationServices.getReservationInfo(key, localObj.reservationid);
        setReserverInfo(info);
      } catch (e) {
        setReserverError("Impossible de récupérer le réservant.");
      } finally {
        setIsLoadingReserver(false);
      }
    }
  };

  return (
    <div className="ui-overlay">
      <div className="ui-modal">
        {showConfirmModal && (
          <div className="ui-overlay">
            <div className="ui-modal">
                            <h3>Confirmer la réservation</h3>
                            <p>Êtes-vous sûr de vouloir marquer ce produit comme réservé ?</p>
                            <p>Les personnes à qui vous partagez vos listes peuvent réserver eux même les produits.</p>
              <div className="ui-buttons">
                <button onClick={() => setShowConfirmModal(false)}>Annuler</button>
                <button onClick={confirmReservation}>Confirmer</button>
              </div>
            </div>
          </div>
        )}

        <button className="oe-close-btn" onClick={() => onCancel()} aria-label="Fermer">
          <img src={logo_close} alt="Fermer" style={{ width: '100%', height: '100%' }} />
        </button>

        <h2 className="oe-title">{readonly ? "Détail de l'article" : "Modifier l'article"}</h2>

        {localObj.imagelink && (
          <div className="oe-image-preview" onClick={() => p_setLightboxImg(object?.imagelink)}>
            <img src={localObj.imagelink} alt="Aperçu" />
          </div>
        )}

        {isReserved && (
          <div className="ov-reserved-banner" role="status" aria-live="polite">
            <span className="ov-reserved-icon" aria-hidden="true">🔒</span>
            <div style={{ flex: 1 }}>
              <span className="ov-reserved-text">Cet article est réservé</span>
              {!readonly && (
                <button type="button" className="oe-link-btn" onClick={handleShowReserver} disabled={isLoadingReserver}>
                  {showReserver ? "Masquer" : "Voir qui a réservé"}
                </button>
              )}
            </div>
          </div>
        )}
        {showReserver && (
          <div className="ov-reserver-details" role="note">
            {isLoadingReserver && <span>Chargement…</span>}
            {!isLoadingReserver && reserverError && <span>{reserverError}</span>}
            {!isLoadingReserver && !reserverError && reserverInfo && (
              <span>Réservé par : {reserverInfo.name}{reserverInfo.email !== "me@me.com" ? ` (${reserverInfo.email})` : ""}</span>
            )}
          </div>
        )}

        <div className="oe-field">
          <label className="oe-label">
            Liste {!localObj.idList && <span className="oe-required">* requis</span>}
          </label>
          <ListSelector
            className="ov-listselector"
            userKey={p_key || AuthServices.getConnectedKey()}
            p_idList={localObj.idList}
            onSelect={(idList) => setLocalObj((prev) => ({ ...prev, idList }))}
            disabled={readonly}
          />
        </div>

        <div className="oe-field">
          <label className="oe-label">Titre</label>
          <input className="oe-input" name="title" value={localObj.title || ''} onChange={handleChange} disabled={readonly} placeholder="Nom de l'article" />
        </div>

        <div className="oe-field">
          <label className="oe-label">Prix</label>
          <input className="oe-input" name="price" type="number" value={localObj.price || ''} onChange={handleChange} disabled={readonly} placeholder="0.00" />
        </div>

        <div className="oe-field">
          <label className="oe-label">Site / Boutique</label>
          <input className="oe-input" name="site" value={localObj.site || ''} onChange={handleChange} disabled={readonly} placeholder="Amazon, Fnac…" />
        </div>

        <div className="oe-field">
          <label className="oe-label">URL du produit</label>
          <input className="oe-input" name="url" value={localObj.url || ''} onChange={handleChange} disabled={readonly} placeholder="https://…" />
        </div>

        <div className="oe-field">
          <label className="oe-label">URL de l'image</label>
          <input className="oe-input" name="imagelink" value={localObj.imagelink || ''} onChange={handleChange} disabled={readonly} placeholder="https://…" />
        </div>

        <div className="oe-field">
          <label className="oe-label">Description</label>
          <textarea className="oe-input oe-textarea" name="description" value={localObj.description || ''} onChange={handleChange} disabled={readonly} rows={3} placeholder="Description optionnelle" />
        </div>

        {completeMsg && <div className="ui-info">{completeMsg}</div>}

        <div className="oe-actions">
          <button className="oe-btn-secondary" onClick={onCancel}>Annuler</button>
          {!readonly && (
            <>
              {hasEmptyFields && !isReserved && (
                <button
                  className="oe-btn-secondary"
                  onClick={handleAutoComplete}
                  disabled={isCompleting || !localObj?.url}
                  title="Compléter automatiquement les champs vides depuis l'URL"
                >
                  {isCompleting ? "Complétion…" : "Auto-compléter"}
                </button>
              )}
              <button className="oe-btn-primary" onClick={handleSave} disabled={!localObj.idList || isDaataRegistering}>
                {isDaataRegistering ? "Enregistrement…" : "Enregistrer"}
              </button>
              {localObj?.id && (
                <button
                  className="oe-btn-reserve"
                  onClick={handleReserve}
                  disabled={!!localObj.reservationid || isDaataRegistering}
                >
                  {localObj?.reservationid ? "Déjà réservé" : "Réserver pour moi"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ObjectEditor;

